import React from "react";
import { ArrowRight, CalendarDays, Flag } from "lucide-react";

import { Card } from "../../ui/card";
import { Button } from "../../ui/button";
import { getStakeholderLaneAppearance } from "../config/stakeholderLaneConfig";

export default function StakeholderCard({ item, onOpen }) {
  const appearance = getStakeholderLaneAppearance(item.lane);
  const disablePersonRouteActions = !!item.disable_person_route_actions;
  const disabledCardClass = disablePersonRouteActions ? "cursor-not-allowed opacity-70" : "";

  return (
    <Card
      className={`border-slate-200/80 bg-white/95 p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-[#11161d] ${appearance.cardHoverClass} ${disabledCardClass}`}
    >
      <button
        className={`block w-full text-left ${disablePersonRouteActions ? "cursor-not-allowed" : ""}`}
        disabled={disablePersonRouteActions}
        onClick={() => onOpen(item.id)}
        type="button"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.title}</p>
              {item.is_kevin_flagged ? (
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${appearance.flagClass}`}
                >
                  <Flag className="h-3 w-3" />
                  {item.urgency_label || "Direct"}
                </span>
              ) : null}
              {item.follow_up_status_label ? (
                <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${appearance.chipClass}`}>
                  {item.follow_up_status_label}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">{item.reason}</p>
          </div>
        </div>
      </button>

      <div className="mt-3 space-y-2 text-xs text-slate-600 dark:text-slate-300">
        {item.display_detail_tags?.length ? (
          <div className="flex flex-wrap gap-1.5">
            {item.display_detail_tags.map((tag) => (
              <span
                className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${appearance.chipClass}`}
                key={tag}
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        {item.last_meaningful_movement ? (
          <div>
            <p className="font-medium text-slate-800 dark:text-slate-100">Last movement</p>
            <p className="mt-0.5 leading-5">{item.last_meaningful_movement}</p>
          </div>
        ) : null}

        {item.latest_contact_summary ? (
          <div>
            <p className="font-medium text-slate-800 dark:text-slate-100">Latest contact</p>
            <p className="mt-0.5 leading-5">{item.latest_contact_summary.replace(/^Last contact:\s*/i, "")}</p>
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-slate-800 dark:text-slate-100">Next move</p>
            <div className="mt-0.5 flex items-start gap-1.5">
              <ArrowRight className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${appearance.accentTextClass}`} />
              <p className="leading-5">{item.next_move}</p>
            </div>
          </div>
          {item.follow_up_date ? (
            <div className={`rounded-full border px-2 py-1 text-[10px] font-medium ${appearance.chipClass}`}>
              <div className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {item.follow_up_date}
              </div>
            </div>
          ) : null}
        </div>

        {item.contact_label ? (
          <div>
            <p className="font-medium text-slate-800 dark:text-slate-100">Contact</p>
            <p className="mt-0.5 leading-5">{item.contact_label}</p>
          </div>
        ) : null}

        {item.next_follow_up_summary ? (
          <div>
            <p className="font-medium text-slate-800 dark:text-slate-100">Follow-up</p>
            <p className="mt-0.5 leading-5">{item.next_follow_up_summary.replace(/^Next follow-up:\s*/i, "")}</p>
          </div>
        ) : null}

        {disablePersonRouteActions && item.person_route_action_note ? (
          <p className="rounded-xl border border-dashed border-slate-200 px-3 py-2 text-[11px] text-slate-500 dark:border-white/10 dark:text-slate-400">
            {item.person_route_action_note}
          </p>
        ) : null}
      </div>

      <Button
        className="mt-3 w-full"
        disabled={disablePersonRouteActions}
        variant="outline"
        onClick={() => onOpen(item.id)}
      >
        Open person
      </Button>
    </Card>
  );
}
